import { endOfISOWeek, format, getISOWeek, startOfISOWeek } from 'date-fns';
import {
  percentOf,
  type SitePlot,
  type WorkStatus,
} from '@billda/design-system';
import type { ActivityRow, ActivitySortDirection } from '../types';

/**
 * Adapter: one activity list response row becomes one ledger row. It maps, it
 * never joins other endpoints or invents facts the row DTO lacks.
 */
export interface ActivityLedgerRowModel {
  id: string;
  isVariation: boolean;
  /** Labour: the definition name. Variation: the work type display name. */
  title: string;
  /** The variation's free-text entry, demoted off the title line. */
  description: string | null;
  /** Who reviewed it and why, once reviewed. */
  approval: ActivityRow['approval'];
  status: WorkStatus;
  reference: string;
  bookedBy: string;
  /** ISO timestamp. */
  bookedAt: string;
  amount: number;
  quantity: number | null;
  unitRate: number | null;
  unitLabel: string | null;
  /** The labour item's full value, when the feed carries one. */
  target: number | null;
  /** Booked share of the target, 0-100, on part-booked rows only. */
  percentBooked: number | null;
  sitePlot: SitePlot;
  siteId: number;
  plotId: number;
}

export interface ActivityWeekGroup {
  /** ISO timestamp of the week start — stable across pages and re-renders. */
  key: string;
  label: string;
  /** The dates the week covers, rendered as the band's secondary label. */
  range: string;
  rows: ActivityLedgerRowModel[];
}

function toRow(item: ActivityRow): ActivityLedgerRowModel {
  const isPartial =
    item.isPartialBooking &&
    item.fullValue != null &&
    item.fullValue > item.value;

  return {
    id: item.id,
    isVariation: item.itemType === 'Variation',
    title: item.title,
    description: item.subtitle ?? null,
    approval: item.approval ?? null,
    status: item.status,
    reference: item.reference,
    bookedBy: item.worker.displayName,
    bookedAt: item.bookedAt,
    amount: item.value,
    quantity: item.quantityClaimed ?? null,
    unitRate: item.unitRate ?? null,
    unitLabel: item.unitLabel ?? null,
    target: item.fullValue ?? null,
    percentBooked:
      isPartial && item.fullValue != null
        ? percentOf(item.value, item.fullValue)
        : null,
    sitePlot: { site: item.siteName, plot: item.plotName },
    siteId: item.siteId,
    plotId: item.plotId,
  };
}

function toGroup(key: string, items: ActivityRow[]): ActivityWeekGroup {
  const start = new Date(key);
  return {
    key,
    label: `Week ${getISOWeek(start)}`,
    range: `${format(start, 'd')}–${format(endOfISOWeek(start), 'd MMM yy')}`,
    rows: items.map(toRow),
  };
}

/** Groups activity rows into ISO weeks in the requested direction. */
export function toActivityRows(
  items: ActivityRow[],
  sortDirection: ActivitySortDirection = 'Descending'
): ActivityWeekGroup[] {
  const byWeek = new Map<string, ActivityRow[]>();
  for (const item of items) {
    const key = startOfISOWeek(new Date(item.bookedAt)).toISOString();
    const existing = byWeek.get(key);
    if (existing) {
      existing.push(item);
    } else {
      byWeek.set(key, [item]);
    }
  }
  return [...byWeek.entries()]
    .sort((a, b) =>
      sortDirection === 'Ascending'
        ? a[0].localeCompare(b[0])
        : b[0].localeCompare(a[0])
    )
    .map(([key, rows]) => toGroup(key, rows));
}
